import { useCallback, useEffect, useRef, useState } from "react";
import { ApolloError, useApolloClient, useSubscription } from "@apollo/client";
import { NEW_MESSAGES_SUBSCRIPTION } from "../graphql/subscriptions";
import { GET_CONVERSATION_MESSAGES_QUERY } from "../graphql/queries";
import { type Message, type MessageContent } from "../components/chat/types";
import { DEFAULT_PAGE_SIZE } from "../constants/pagination";
import { CursorUtils } from "../utils/cursorUtils";
import { type MessageSenderRole } from "../generated/graphql";

export interface ConnectionState {
  isConnecting: boolean;
  isConnected: boolean;
  transport: 'websocket' | 'sse' | 'unknown';
  lastConnected?: Date;
  reconnectCount: number;
  lastError?: string;
  reconnectDelay?: number;
}

interface MessageSubscriptionData {
  newMessages: {
    message: {
      id: string;
      conversationId: string;
      senderRole: MessageSenderRole;
      content: Record<string, unknown>;
      timestamp: string;
      metadata?: Record<string, unknown>;
    };
    conversationId: string;
    eventType: string;
  };
}

interface UseMessageSubscriptionProps {
  conversationId: string;
  onNewMessage?: (message: Message) => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
  enabled?: boolean;
  maxReconnectAttempts?: number;
}

// Maximum reconnection attempts before giving up
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 10;

// Base delay for reconnection (milliseconds)
const BASE_RECONNECT_DELAY = 1000;

// Maximum delay for reconnection (milliseconds)
const MAX_RECONNECT_DELAY = 30000;

export function useMessageSubscription({
  conversationId,
  onNewMessage,
  onConnectionStateChange,
  enabled = true,
  maxReconnectAttempts = DEFAULT_MAX_RECONNECT_ATTEMPTS,
}: UseMessageSubscriptionProps) {
  const onNewMessageRef = useRef(onNewMessage);
  onNewMessageRef.current = onNewMessage;
  const onConnectionStateChangeRef = useRef(onConnectionStateChange);
  onConnectionStateChangeRef.current = onConnectionStateChange;
  
  const client = useApolloClient();
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isUnmountedRef = useRef(false);
  
  // Track connection state
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isConnecting: false,
    isConnected: false,
    transport: 'websocket', // Default to WebSocket
    reconnectCount: 0,
  });

  // Calculate reconnect delay with exponential backoff
  const getReconnectDelay = useCallback((attemptNumber: number) => {
    const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, attemptNumber), MAX_RECONNECT_DELAY);
    // Add jitter to prevent thundering herd
    return delay + Math.random() * 1000;
  }, []);

  // Enhanced error categorization for better handling
  const categorizeError = useCallback((error: ApolloError | Error): 'auth' | 'network' | 'server' | 'connection_limit' | 'rate_limit' | 'protocol' | 'unknown' => {
    const errorMessage = error.message.toLowerCase();
    const apolloError = error as ApolloError;
    const errorCode = apolloError.graphQLErrors?.[0]?.extensions?.code || apolloError.networkError?.message;
    
    // Authentication errors
    if (errorMessage.includes('unauthorized') || 
        errorMessage.includes('authentication') || 
        errorMessage.includes('forbidden') ||
        errorMessage.includes('401') ||
        errorCode === 'AUTHENTICATION_REQUIRED' ||
        errorCode === 'UNAUTHENTICATED' ||
        errorCode === 'UNAUTHORIZED') {
      return 'auth';
    }
    
    // Connection limit errors
    if (errorMessage.includes('connection limit') ||
        errorMessage.includes('too many connections') ||
        errorCode === 'CONNECTION_LIMIT_EXCEEDED') {
      return 'connection_limit';
    }
    
    // Rate limit errors  
    if (errorMessage.includes('rate limit') ||
        errorMessage.includes('too many requests') ||
        errorCode === 'RATE_LIMITED' ||
        errorCode === 'TOO_MANY_REQUESTS') {
      return 'rate_limit';
    }
    
    // Protocol errors
    if (errorMessage.includes('protocol') ||
        errorMessage.includes('invalid frame') ||
        errorMessage.includes('websocket') ||
        errorCode === 'PROTOCOL_ERROR') {
      return 'protocol';
    }
    
    // Network errors
    if (errorMessage.includes('network') || 
        errorMessage.includes('fetch') || 
        errorMessage.includes('connection') ||
        errorMessage.includes('timeout') ||
        errorCode === 'NETWORK_ERROR') {
      return 'network';
    }
    
    // Server errors
    if (errorMessage.includes('500') || 
        errorMessage.includes('internal server') ||
        errorMessage.includes('server error') ||
        errorCode === 'INTERNAL_SERVER_ERROR') {
      return 'server';
    }
    
    return 'unknown';
  }, []);

  // Helper function to ensure content is properly typed
  const parseMessageContent = (content: unknown): MessageContent => {
    // Content should already be an object from the backend
    if (typeof content === 'object' && content !== null) {
      return content as MessageContent;
    }
    // Fallback for unexpected content types
    console.warn('Unexpected content type:', typeof content, content);
    return { text: String(content) };
  };

  // Update connection state when it changes
  useEffect(() => {
    onConnectionStateChangeRef.current?.(connectionState);
  }, [connectionState]);

  const { data, loading, error } = useSubscription<MessageSubscriptionData>(
    NEW_MESSAGES_SUBSCRIPTION,
    {
      variables: { conversationId },
      skip: !enabled || !conversationId,
      onData: ({ data: subscriptionData }) => {
        // Update connection state when we receive data
        setConnectionState(prev => ({
          ...prev,
          isConnected: true,
          isConnecting: false,
          lastConnected: new Date(),
        }));

        if (subscriptionData?.data?.newMessages) {
          const { message } = subscriptionData.data.newMessages;
          console.log('[Subscription] Received new message:', {
            messageId: message.id,
            conversationId: message.conversationId,
            senderRole: message.senderRole,
            transport: connectionState.transport,
            timestamp: new Date().toISOString()
          });

          // Convert GraphQL message to frontend Message type
          const frontendMessage: Message = {
            id: message.id,
            conversationId: message.conversationId,
            senderRole: message.senderRole,
            content: parseMessageContent(message.content),
            timestamp: message.timestamp,
            metadata: message.metadata,
          };

          // Update Apollo cache to include the new message in connection format
          let existingData = null;
          let usedVariables = null;
          
          try {
            // First, try to find the existing query in cache with different variable combinations
            
            // Try common variable combinations
            const variableCombinations = [
              // Default forward pagination
              { conversationId, first: DEFAULT_PAGE_SIZE },
              // With null values explicitly set
              { conversationId, first: DEFAULT_PAGE_SIZE, after: null, last: null, before: null },
              // Backward pagination (what ChatPage actually uses)
              { conversationId, last: 20 },
              { conversationId, last: DEFAULT_PAGE_SIZE },
              // Minimal variables (what useConversationMessages might use)
              { conversationId },
            ];
            
            for (const vars of variableCombinations) {
              try {
                existingData = client.readQuery({
                  query: GET_CONVERSATION_MESSAGES_QUERY,
                  variables: vars,
                });
                if (existingData) {
                  usedVariables = vars;
                  console.log('[Subscription] Found cache entry with variables:', JSON.stringify(vars));
                  break;
                }
              } catch (_e) {
                // Continue trying other combinations
              }
            }
            
            if (!existingData) {
              console.log('[Subscription] No existing cache entry found for any variable combination');
              console.log('[Subscription] Tried combinations:', JSON.stringify(variableCombinations, null, 2));
              // Use the default variables for writing new cache entry
              usedVariables = { conversationId, first: DEFAULT_PAGE_SIZE };
            }

            if (existingData?.getConversationMessages) {
              const connection = existingData.getConversationMessages;

              // Check if message already exists to prevent duplicates
              const messageExists = connection.edges.some(
                (edge: { node: { id: string } }) => edge.node.id === message.id,
              );

              if (!messageExists) {
                // Create new edge for the message
                const newEdge = {
                  __typename: "ChatMessageEdge" as const,
                  cursor: CursorUtils.encode(message.timestamp, message.id),
                  node: {
                    __typename: "ChatMessage" as const,
                    id: message.id,
                    conversationId: message.conversationId,
                    senderRole: message.senderRole,
                    content: message.content, // Keep raw content for cache consistency
                    timestamp: message.timestamp,
                    metadata: message.metadata || null,
                  },
                };

                // Add new edge to existing edges
                const updatedConnection = {
                  ...connection,
                  edges: [...connection.edges, newEdge],
                  totalCount: connection.totalCount ? connection.totalCount + 1 : 1,
                  pageInfo: {
                    ...connection.pageInfo,
                    endCursor: newEdge.cursor,
                  },
                };

                console.log('[Subscription] Writing to Apollo cache:', {
                  newMessageId: message.id,
                  totalMessages: updatedConnection.edges.length,
                  endCursor: updatedConnection.pageInfo.endCursor,
                  variables: usedVariables,
                });
                
                // Try both writeQuery and cache.modify for maximum compatibility
                try {
                  if (usedVariables) {
                    client.writeQuery({
                      query: GET_CONVERSATION_MESSAGES_QUERY,
                      variables: usedVariables,
                      data: {
                        getConversationMessages: updatedConnection,
                      },
                      broadcast: true, // Ensure cache update triggers re-renders
                    });
                  }
                  
                  console.log('[Subscription] Cache updated successfully with writeQuery');
                } catch (writeError) {
                  console.error('[Subscription] writeQuery failed, trying cache.modify:', writeError);
                  
                  // Fallback to cache.modify which is more flexible
                  client.cache.modify({
                    fields: {
                      getConversationMessages(_existingRef = {}, { readField: _readField }) {
                        console.log('[Subscription] Using cache.modify to update messages');
                        return updatedConnection;
                      }
                    },
                    broadcast: true,
                  });
                }
              }
            }
          } catch (error) {
            // If the query hasn't been executed yet, the cache read will fail
            // This is expected and we can safely ignore it
            console.warn("[Subscription] Cache update failed (non-critical):", error);
            console.log("[Subscription] Cache update details:", {
              hasQuery: !!existingData,
              usedVariables,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
            // IMPORTANT: Don't let cache update failures break the subscription
            // The message was still received and the callback can handle it
          }

          // Call the callback if provided
          onNewMessageRef.current?.(frontendMessage);
        }
      },
      onError: subscriptionError => {
        console.error("[Subscription] Error:", subscriptionError);
        
        const errorCategory = categorizeError(subscriptionError);
        const currentReconnectCount = connectionState.reconnectCount;
        
        // Update connection state on error
        setConnectionState(prev => {
          const nextReconnectCount = prev.reconnectCount + 1;
          const reconnectDelay = getReconnectDelay(nextReconnectCount);
          
          return {
            ...prev,
            isConnected: false,
            isConnecting: false,
            reconnectCount: nextReconnectCount,
            lastError: subscriptionError.message,
            reconnectDelay,
          };
        });

        // Handle different error types with enhanced categorization
        switch (errorCategory) {
          case 'auth':
            console.error('[Subscription] Authentication error - user may need to re-login');
            // Don't attempt reconnection for auth errors
            break;
            
          case 'connection_limit':
            console.error('[Subscription] Connection limit exceeded - too many concurrent connections');
            // Don't retry immediately for connection limit errors
            break;
            
          case 'rate_limit':
            console.error('[Subscription] Rate limit exceeded - too many requests');
            // Don't retry immediately for rate limit errors
            break;
            
          case 'protocol':
            console.error('[Subscription] WebSocket protocol error - may indicate server incompatibility');
            // Limited retry for protocol errors
            if (currentReconnectCount < 3 && !isUnmountedRef.current) {
              const delay = getReconnectDelay(currentReconnectCount + 1) * 2; // Longer delay for protocol errors
              console.log(`[Subscription] Will retry protocol error in ${Math.round(delay)}ms (attempt ${currentReconnectCount + 1}/3)`);
              
              if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
              }
              
              reconnectTimerRef.current = setTimeout(() => {
                if (!isUnmountedRef.current && enabled) {
                  console.log('[Subscription] Attempting to reconnect after protocol error...');
                  setConnectionState(prev => ({
                    ...prev,
                    isConnecting: true,
                  }));
                }
              }, delay);
            }
            break;
            
          case 'network':
          case 'server':
            // Attempt reconnection for network/server errors
            if (currentReconnectCount < maxReconnectAttempts && !isUnmountedRef.current) {
              const delay = getReconnectDelay(currentReconnectCount + 1);
              console.log(`[Subscription] Will retry ${errorCategory} error in ${Math.round(delay)}ms (attempt ${currentReconnectCount + 1}/${maxReconnectAttempts})`);
              
              // Clear existing timer
              if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
              }
              
              // Schedule reconnection
              reconnectTimerRef.current = setTimeout(() => {
                if (!isUnmountedRef.current && enabled) {
                  console.log(`[Subscription] Attempting to reconnect after ${errorCategory} error...`);
                  // Apollo will automatically retry the subscription
                  setConnectionState(prev => ({
                    ...prev,
                    isConnecting: true,
                  }));
                }
              }, delay);
            } else if (currentReconnectCount >= maxReconnectAttempts) {
              console.error(`[Subscription] Max reconnection attempts (${maxReconnectAttempts}) reached for ${errorCategory} error`);
            }
            break;
            
          default:
            console.error(`[Subscription] Unknown error type (${errorCategory}):`, subscriptionError.message);
            // Default retry behavior for unknown errors
            if (currentReconnectCount < maxReconnectAttempts && !isUnmountedRef.current) {
              const delay = getReconnectDelay(currentReconnectCount + 1);
              console.log(`[Subscription] Will retry unknown error in ${Math.round(delay)}ms (attempt ${currentReconnectCount + 1}/${maxReconnectAttempts})`);
              
              if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
              }
              
              reconnectTimerRef.current = setTimeout(() => {
                if (!isUnmountedRef.current && enabled) {
                  console.log('[Subscription] Attempting to reconnect after unknown error...');
                  setConnectionState(prev => ({
                    ...prev,
                    isConnecting: true,
                  }));
                }
              }, delay);
            }
        }
      },
      onComplete: () => {
        console.log("[Subscription] Subscription completed");
        
        // Update connection state on completion
        setConnectionState(prev => ({
          ...prev,
          isConnected: false,
          isConnecting: false,
        }));
        
        // For WebSocket, completion might mean the server closed the connection
        // The WebSocket link will handle reconnection automatically
        console.log("[Subscription] WebSocket connection will reconnect automatically");
      },
    },
  );

  useEffect(() => {
    if (enabled && conversationId) {
      console.log(`[Subscription] Starting subscription for conversation: ${conversationId}`);
      
      // Reset unmount flag
      isUnmountedRef.current = false;
      
      // Set connecting state and reset error state
      setConnectionState(prev => ({
        ...prev,
        isConnecting: true,
        isConnected: false,
        lastError: undefined,
        reconnectDelay: undefined,
      }));
    }

    return () => {
      // Set unmount flag
      isUnmountedRef.current = true;
      
      // Clear any pending reconnection timers
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      
      if (enabled && conversationId) {
        console.log(`[Subscription] Cleaning up subscription for conversation: ${conversationId}`);
        
        // Reset connection state
        setConnectionState({
          isConnecting: false,
          isConnected: false,
          transport: 'websocket',
          reconnectCount: 0,
          lastError: undefined,
          reconnectDelay: undefined,
        });
      }
    };
  }, [conversationId, enabled]);

  return {
    subscriptionData: data,
    subscriptionLoading: loading,
    subscriptionError: error,
    connectionState,
  };
}
